import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GroupsIcon from '@mui/icons-material/Groups';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import BlockIcon from '@mui/icons-material/Block';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const STATUS_META = {
  pending: {
    label: 'Pending',
    borderColor: 'grey.400',
    icon: <InfoOutlinedIcon fontSize="small" />,
    chipColor: 'default',
    tooltip: 'Ready for execution',
  },
  unedited: {
    label: 'Unedited',
    borderColor: 'grey.300',
    icon: <InfoOutlinedIcon fontSize="small" />,
    chipColor: 'default',
    tooltip: 'No criteria defined',
  },
  edited: {
    label: 'Edited',
    borderColor: 'info.main',
    icon: <EditNoteIcon fontSize="small" />,
    chipColor: 'info',
    tooltip: 'Criteria defined (not executed)',
  },
  executing: {
    label: 'Executing',
    borderColor: 'warning.main',
    icon: <CircularProgress size={16} />,
    chipColor: 'warning',
    tooltip: 'Calculation in progress',
  },
  executed: {
    label: 'Executed',
    borderColor: 'success.main',
    icon: <CheckCircleIcon fontSize="small" />,
    chipColor: 'success',
    tooltip: 'Patient count calculated',
  },
  completed: {
    label: 'Completed',
    borderColor: 'success.main',
    icon: <CheckCircleIcon fontSize="small" />,
    chipColor: 'success',
    tooltip: 'Execution completed',
  },
  error: {
    label: 'Error',
    borderColor: 'error.main',
    icon: <ErrorOutlineIcon fontSize="small" />,
    chipColor: 'error',
    tooltip: 'Execution failed',
  },
  unmapped: {
    label: 'Unmapped',
    borderColor: 'warning.main',
    icon: <ErrorOutlineIcon fontSize="small" />,
    chipColor: 'warning',
    tooltip: 'The server could not translate this criterion',
  },
};

const CohortNode = ({
  id,
  label,
  description = '',
  criteria,
  criteriaCount,
  patientCount,
  isExecuted,
  status = 'unedited',
  isBreakpoint = false,
  selected = false,
  compact = false,
  onEdit,
  onDelete,
  onAddChild,
  onInsert,
  onExecuteToHere,
  onToggleBreakpoint,
  onRename,
}) => {
  const meta = STATUS_META[status] || STATUS_META.unedited;

  const executed = status === 'executed' || status === 'completed' || isExecuted === true;

  const includeCount = useMemo(() => {
    if (typeof criteriaCount?.include === 'number') return criteriaCount.include;
    if (Array.isArray(criteria?.include)) return criteria.include.length;
    return undefined;
  }, [criteria, criteriaCount]);

  const excludeCount = useMemo(() => {
    if (typeof criteriaCount?.exclude === 'number') return criteriaCount.exclude;
    if (Array.isArray(criteria?.exclude)) return criteria.exclude.length;
    return undefined;
  }, [criteria, criteriaCount]);

  const criteriaSummaryText = useMemo(() => {
    if (typeof includeCount === 'number' || typeof excludeCount === 'number') {
      const inc = typeof includeCount === 'number' ? includeCount : 0;
      const exc = typeof excludeCount === 'number' ? excludeCount : 0;
      return `${inc} Include, ${exc} Exclude`;
    }
    return 'No criteria defined';
  }, [excludeCount, includeCount]);

  const includeCriteriaPreview = useMemo(
    () =>
      Array.isArray(criteria?.include)
        ? criteria.include
            .slice(0, 3)
            .map((criterion) =>
              `${criterion?.field || criterion?.parameterName || 'Condition'} ${
                criterion?.operator || '='
              } ${criterion?.value || ''}`.trim(),
            )
        : [],
    [criteria?.include],
  );

  const excludeCriteriaPreview = useMemo(
    () =>
      Array.isArray(criteria?.exclude)
        ? criteria.exclude
            .slice(0, 3)
            .map((criterion) =>
              `${criterion?.field || criterion?.parameterName || 'Condition'} ${
                criterion?.operator || '='
              } ${criterion?.value || ''}`.trim(),
            )
        : [],
    [criteria?.exclude],
  );

  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [draftLabel, setDraftLabel] = useState(label || '');
  const [contextMenuPosition, setContextMenuPosition] = useState(null);
  const [detailsAnchorEl, setDetailsAnchorEl] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraftLabel(label || '');
  }, [label]);

  useEffect(() => {
    if (isEditingLabel && inputRef.current) {
      inputRef.current.focus();
      try {
        inputRef.current.select();
      } catch {
        // ignore
      }
    }
  }, [isEditingLabel]);

  const commitLabel = () => {
    const next = (draftLabel || '').trim();
    if (!next) {
      setDraftLabel(label || '');
      setIsEditingLabel(false);
      return;
    }

    if (next !== label) {
      onRename?.(id, next);
    }
    setIsEditingLabel(false);
  };

  // ReactFlow uses pointer/mouse down to start drag/pan. We must stop propagation early
  // for interactive UI elements inside the node, otherwise clicks can be swallowed.
  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const stopAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const triggerAction = (e, action) => {
    stopAll(e);
    action?.(id);
  };

  const closeContextMenu = useCallback(() => {
    setContextMenuPosition(null);
  }, []);

  const closeDetails = useCallback(() => {
    setDetailsAnchorEl(null);
  }, []);

  const openContextMenuAt = (x, y) => {
    setContextMenuPosition({ top: y, left: x });
  };

  const handleOpenContextMenuFromPointer = (e) => {
    stopAll(e);
    openContextMenuAt(e.clientX + 2, e.clientY - 4);
  };

  const handleOpenContextMenuFromButton = (e) => {
    stopAll(e);
    const rect = e.currentTarget?.getBoundingClientRect?.();
    if (rect) {
      openContextMenuAt(rect.left + rect.width / 2, rect.bottom + 6);
      return;
    }
    openContextMenuAt(e.clientX + 2, e.clientY - 4);
  };

  const handleOpenDetails = (e) => {
    stopAll(e);
    setDetailsAnchorEl(e.currentTarget);
  };

  const handleOpenContextMenuFromKeyboard = (e) => {
    stopAll(e);
    const rect = e.currentTarget?.getBoundingClientRect?.();
    if (rect) {
      openContextMenuAt(rect.left + rect.width / 2, rect.top + rect.height / 2);
      return;
    }
    openContextMenuAt(0, 0);
  };

  const runMenuAction = (e, action) => {
    stopAll(e);
    closeContextMenu();
    action?.(id);
  };

  return (
    <>
      <Card
        onClick={() => onEdit?.(id)}
        onContextMenu={compact ? undefined : handleOpenContextMenuFromPointer}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            stopAll(e);
            onEdit?.(id);
            return;
          }

          if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
            handleOpenContextMenuFromKeyboard(e);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Cohort node ${label || id}. ${criteriaSummaryText}.`}
        sx={{
          width: compact ? 220 : 320,
          borderRadius: 2,
          border: '1px solid',
          borderColor: selected ? 'primary.main' : meta.borderColor,
          transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
          boxShadow: selected ? '0 10px 24px rgba(0,0,0,0.12)' : '0 2px 10px rgba(0,0,0,0.06)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: selected ? '0 12px 28px rgba(0,0,0,0.16)' : '0 8px 22px rgba(0,0,0,0.12)',
            borderColor: selected ? 'primary.main' : meta.borderColor,
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
          userSelect: 'none',
        }}
      >
        <Box sx={{ px: 1.25, pt: 1.25, pb: compact ? 1 : 0.75 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Tooltip title={meta.tooltip}>
                <Box
                  sx={{
                    color: selected ? 'primary.main' : meta.borderColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {meta.icon}
                </Box>
              </Tooltip>

              <Box sx={{ minWidth: 0 }}>
                {isEditingLabel ? (
                  <TextField
                    inputRef={inputRef}
                    className="nodrag"
                    value={draftLabel}
                    onChange={(e) => setDraftLabel(e.target.value)}
                    onMouseDown={stopPropagation}
                    onPointerDown={stopPropagation}
                    onClick={stopAll}
                    onDoubleClick={stopAll}
                    onBlur={commitLabel}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        stopAll(e);
                        commitLabel();
                      }
                      if (e.key === 'Escape') {
                        stopAll(e);
                        setDraftLabel(label || '');
                        setIsEditingLabel(false);
                      }
                    }}
                    variant="standard"
                    fullWidth
                    size="small"
                  />
                ) : (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.15,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      cursor: 'default',
                    }}
                    title={label}
                    onClick={stopAll}
                    onDoubleClick={(e) => {
                      stopAll(e);
                      setIsEditingLabel(true);
                    }}
                    onMouseDown={stopPropagation}
                    onPointerDown={stopPropagation}
                  >
                    {label || 'Unnamed cohort'}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.25} alignItems="center" className="nodrag" sx={{ flexShrink: 0 }}>
              <Chip size="small" label={meta.label} color={meta.chipColor} variant="outlined" sx={{ fontWeight: 700 }} />

              {compact && executed && typeof patientCount === 'number' ? (
                <Chip
                  size="small"
                  icon={<GroupsIcon sx={{ fontSize: 16 }} />}
                  label={patientCount.toLocaleString()}
                  sx={{ fontWeight: 800 }}
                />
              ) : null}

              {!compact ? (
                <Tooltip title="Node details">
                  <IconButton
                    size="small"
                    aria-label={`Open details for node ${label || id}`}
                    className="nodrag"
                    onPointerDown={stopPropagation}
                    onClick={handleOpenDetails}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}

              {!compact ? (
                <Tooltip title="Node actions menu">
                  <IconButton
                    size="small"
                    aria-label={`Open node menu for ${label || id}`}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(contextMenuPosition)}
                    className="nodrag"
                    onPointerDown={stopPropagation}
                    onClick={handleOpenContextMenuFromButton}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleOpenContextMenuFromButton(e);
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>

          {!compact ? (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 0.75, minHeight: 24 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  minWidth: 0,
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={criteriaSummaryText}
              >
                {criteriaSummaryText}
              </Typography>

              {isBreakpoint ? (
                <Tooltip title='Execution stops at this node when using "Execute to Breakpoint".'>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<OutlinedFlagIcon sx={{ fontSize: 14 }} />}
                    label="BP"
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
              ) : null}

              {executed && typeof patientCount === 'number' ? (
                <Chip
                  size="small"
                  icon={<GroupsIcon sx={{ fontSize: 16 }} />}
                  label={patientCount.toLocaleString()}
                  sx={{ fontWeight: 800 }}
                />
              ) : null}
            </Stack>
          ) : null}

          {!compact ? (
            <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} className="nodrag">
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  aria-label={`Edit node ${label || id}`}
                  className="nodrag"
                  onPointerDown={(e) => triggerAction(e, onEdit)}
                  onClick={stopAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') triggerAction(e, onEdit);
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add child node">
                <IconButton
                  size="small"
                  aria-label={`Add child node for ${label || id}`}
                  className="nodrag"
                  onPointerDown={(e) => triggerAction(e, onAddChild)}
                  onClick={stopAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') triggerAction(e, onAddChild);
                  }}
                >
                  <AddCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  aria-label={`Delete node ${label || id}`}
                  className="nodrag"
                  onPointerDown={(e) => triggerAction(e, onDelete)}
                  onClick={stopAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') triggerAction(e, onDelete);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null}
        </Box>

        {!compact ? (
          <>
            <Divider />
            <CardContent sx={{ pt: 1.25, pb: 1.25 }}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap">
                {typeof includeCount === 'number' ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<PlaylistAddCheckIcon sx={{ fontSize: 16 }} />}
                    label={`${includeCount} include`}
                  />
                ) : null}
                {typeof excludeCount === 'number' ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<BlockIcon sx={{ fontSize: 16 }} />}
                    label={`${excludeCount} exclude`}
                  />
                ) : null}
                {typeof includeCount !== 'number' && typeof excludeCount !== 'number' ? (
                  <Typography variant="caption" color="text.secondary">
                    No criteria yet. Double-click the title to rename.
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </>
        ) : null}
      </Card>

      <Menu
        open={Boolean(contextMenuPosition)}
        onClose={closeContextMenu}
        MenuListProps={{
          'aria-label': `Actions for node ${label || id}`,
        }}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuPosition
            ? { top: contextMenuPosition.top, left: contextMenuPosition.left }
            : undefined
        }
      >
        <MenuItem aria-label={`Edit node ${label || id}`} onClick={(e) => runMenuAction(e, onEdit)} disabled={!onEdit}>
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Node</ListItemText>
        </MenuItem>
        <MenuItem
          aria-label={`Add child node for ${label || id}`}
          onClick={(e) => runMenuAction(e, onAddChild)}
          disabled={!onAddChild}
        >
          <ListItemIcon>
            <AddCircleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add Child</ListItemText>
        </MenuItem>
        <MenuItem aria-label={`Delete node ${label || id}`} onClick={(e) => runMenuAction(e, onDelete)} disabled={!onDelete}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Node</ListItemText>
        </MenuItem>
        <MenuItem
          aria-label={`Execute graph to node ${label || id}`}
          onClick={(e) => runMenuAction(e, onExecuteToHere)}
          disabled={!onExecuteToHere}
        >
          <ListItemIcon>
            <PlayArrowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Execute to Here</ListItemText>
        </MenuItem>
        <MenuItem
          aria-label={`Insert node before ${label || id}`}
          onClick={(e) => runMenuAction(e, onInsert)}
          disabled={!onInsert}
        >
          <ListItemIcon>
            <CallSplitIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Insert Node</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          aria-label={isBreakpoint ? 'Remove breakpoint' : 'Set breakpoint'}
          onClick={(e) => runMenuAction(e, onToggleBreakpoint)}
          disabled={!onToggleBreakpoint}
        >
          <ListItemIcon>
            <OutlinedFlagIcon fontSize="small" color={isBreakpoint ? 'warning' : 'inherit'} />
          </ListItemIcon>
          <ListItemText>{isBreakpoint ? 'Remove Breakpoint' : 'Set as Breakpoint'}</ListItemText>
        </MenuItem>
      </Menu>

      <Popover
        open={Boolean(detailsAnchorEl)}
        anchorEl={detailsAnchorEl}
        onClose={closeDetails}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1.5, width: 320, maxWidth: '80vw' }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {label || `Node ${id}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {description ? description : 'No description yet.'}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              <Chip size="small" label={meta.label} color={meta.chipColor} variant="outlined" />
              <Chip
                size="small"
                variant="outlined"
                icon={<PlaylistAddCheckIcon sx={{ fontSize: 14 }} />}
                label={`${typeof includeCount === 'number' ? includeCount : 0} include`}
              />
              <Chip
                size="small"
                variant="outlined"
                icon={<BlockIcon sx={{ fontSize: 14 }} />}
                label={`${typeof excludeCount === 'number' ? excludeCount : 0} exclude`}
              />
              {executed && typeof patientCount === 'number' ? (
                <Chip size="small" icon={<GroupsIcon sx={{ fontSize: 14 }} />} label={`${patientCount.toLocaleString()} patients`} />
              ) : null}
            </Stack>
            {includeCriteriaPreview.length > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Include
                </Typography>
                {includeCriteriaPreview.map((entry, index) => (
                  <Typography key={`inc-${index}`} variant="caption" sx={{ display: 'block' }} color="text.secondary">
                    • {entry}
                  </Typography>
                ))}
              </Box>
            ) : null}
            {excludeCriteriaPreview.length > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Exclude
                </Typography>
                {excludeCriteriaPreview.map((entry, index) => (
                  <Typography key={`exc-${index}`} variant="caption" sx={{ display: 'block' }} color="text.secondary">
                    • {entry}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default React.memo(CohortNode);
